import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import api from '../lib/api';
import { 
  Globe,
  Users, 
  UserCheck,
  EyeOff,
  MessageSquare,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  Filter,
  ShieldAlert,
  Hash,
  Type,
  SlidersHorizontal,
  ListChecks,
  ChevronDown,
  ChevronRight,
  List,
  Send,
  FileText,
  Lightbulb
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#5AA2AE', '#9b87f5', '#22c55e', '#f59e0b', '#ef4444', '#6366f1', '#84cc16', '#06b6d4'];

// Helper function to format score with one decimal when denominator <= 10
function formatScore(score, denominator = 10) {
  if (score === null || score === undefined) return '-';
  const numScore = parseFloat(score);
  if (isNaN(numScore)) return '-';
  if (denominator <= 10) {
    return numScore.toFixed(1);
  }
  return Math.round(numScore).toString();
}

// Question type icon
function QuestionTypeIcon({ type }) {
  const icons = {
    slider: <SlidersHorizontal className="w-4 h-4" />,
    scale: <SlidersHorizontal className="w-4 h-4" />,
    free_text: <Type className="w-4 h-4" />,
    multi_choice: <List className="w-4 h-4" />,
    single_choice: <List className="w-4 h-4" />,
    checkbox: <ListChecks className="w-4 h-4" />,
    multi_topic: <Hash className="w-4 h-4" />,
  };
  return icons[type] || <MessageSquare className="w-4 h-4" />;
}

// Slider visualization with distribution histogram (from Driver Analysis)
function SliderVisualization({ responses, config }) {
  if (!responses || responses.length === 0) return null;
  
  const minVal = config?.min_value || 0;
  const maxVal = config?.max_value || 10;
  const leftLabel = config?.left_label || 'Low';
  const middleLabel = config?.middle_label || '';
  const rightLabel = config?.right_label || 'High';
  
  // Calculate average
  const values = responses.map(r => parseFloat(r.value || r.answer)).filter(v => !isNaN(v));
  const average = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  
  // Calculate standard deviation
  const stdDev = values.length > 1 
    ? Math.sqrt(values.reduce((sum, v) => sum + Math.pow(v - average, 2), 0) / values.length)
    : 0;
  
  // Prepare distribution data (5 buckets)
  const bucketSize = (maxVal - minVal) / 5;
  const distribution = [0, 0, 0, 0, 0];
  values.forEach(v => {
    const bucketIndex = Math.min(4, Math.floor((v - minVal) / bucketSize));
    distribution[bucketIndex]++;
  });
  
  const bucketLabels = ['0-2', '2-4', '4-6', '6-8', '8-10'];
  const distributionData = distribution.map((count, i) => ({
    range: bucketLabels[i],
    count,
    fill: i === Math.floor(average / 2) ? '#5AA2AE' : '#94a3b8'
  }));
  
  // Calculate percentage position for average marker
  const avgPercent = ((average - minVal) / (maxVal - minVal)) * 100;
  
  return (
    <div className="space-y-4">
      {/* Average display */}
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-slate-900">{formatScore(average, maxVal)}</span>
          <span className="text-sm text-slate-500">/ {maxVal}</span>
        </div>
        {stdDev > 0 && (
          <Badge variant="outline" className="text-slate-500">
            σ = {stdDev.toFixed(2)}
          </Badge>
        )}
      </div>
      
      {/* Slider bar with average marker */}
      <div className="relative">
        <div className="h-3 bg-gradient-to-r from-red-200 via-amber-200 to-emerald-200 rounded-full" />
        <div 
          className="absolute top-0 w-1 h-3 bg-slate-900 rounded"
          style={{ left: `${avgPercent}%`, transform: 'translateX(-50%)' }}
        />
      </div>
      
      {/* Labels */}
      <div className="flex justify-between text-xs text-slate-500">
        <span>{leftLabel}</span>
        {middleLabel && <span>{middleLabel}</span>}
        <span>{rightLabel}</span>
      </div>
      
      {/* Distribution histogram */}
      <div className="h-24 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={distributionData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <XAxis dataKey="range" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis hide />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {distributionData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Choice visualization (from Driver Analysis)
function ChoiceVisualization({ responses, multiSelect }) {
  if (!responses || responses.length === 0) return null;
  
  const distribution = {};
  responses.forEach(r => {
    const val = r.value || r.answer;
    if (Array.isArray(val)) {
      val.forEach(v => { distribution[v] = (distribution[v] || 0) + 1; });
    } else if (val) {
      distribution[val] = (distribution[val] || 0) + 1;
    }
  });
  
  const totalResponses = responses.length;
  const options = Object.entries(distribution)
    .map(([label, count]) => ({
      label,
      count,
      percentage: Math.round((count / totalResponses) * 100)
    }))
    .sort((a, b) => b.count - a.count);
  
  const colors = ['#5AA2AE', '#7BC4CF', '#9DD5DE', '#BFE6EC', '#E0F4F7'];
  
  return (
    <div className="space-y-3">
      {options.slice(0, 8).map((opt, i) => (
        <div key={i}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="truncate flex-1 mr-2 text-slate-700">{opt.label}</span>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-slate-500 font-medium">{opt.count}</span>
              <span className="text-slate-400 text-xs">({opt.percentage}%)</span>
            </div>
          </div>
          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${opt.percentage}%`, backgroundColor: colors[i % colors.length] }}
            />
          </div>
        </div>
      ))}
      {options.length > 8 && (
        <p className="text-xs text-slate-400">+{options.length - 8} more options</p>
      )}
      {multiSelect && (
        <p className="text-xs text-slate-400 italic">Multi-select: percentages based on {totalResponses} respondents</p>
      )}
    </div>
  );
}

// Free text visualization with sentiment (from Driver Analysis)
function FreeTextVisualization({ responses, question, onAnalyze }) {
  if (!responses || responses.length === 0) return null;
  
  // Calculate average length
  const texts = responses.map(r => (r.value || r.answer || '').toString()).filter(t => t.length > 0);
  const avgLength = texts.length > 0 
    ? Math.round(texts.reduce((sum, t) => sum + t.length, 0) / texts.length)
    : 0;
  
  // Simple sentiment analysis (basic keyword matching)
  let positive = 0, negative = 0, neutral = 0;
  const positiveWords = ['great', 'good', 'excellent', 'happy', 'love', 'support', 'excited', 'opportunity', 'benefit'];
  const negativeWords = ['bad', 'concern', 'worried', 'fear', 'noise', 'pollution', 'traffic', 'damage', 'problem', 'issue'];
  
  texts.forEach(text => {
    const lower = text.toLowerCase();
    const hasPositive = positiveWords.some(w => lower.includes(w));
    const hasNegative = negativeWords.some(w => lower.includes(w));
    
    if (hasPositive && !hasNegative) positive++;
    else if (hasNegative && !hasPositive) negative++;
    else neutral++;
  });
  
  const total = positive + negative + neutral;
  const sentimentBreakdown = total > 0 ? {
    positive: Math.round((positive / total) * 100),
    neutral: Math.round((neutral / total) * 100),
    negative: Math.round((negative / total) * 100)
  } : null;
  
  const sentimentColors = {
    positive: '#10b981',
    negative: '#ef4444', 
    neutral: '#6b7280'
  };
  
  return (
    <div className="space-y-4">
      {/* Stats row with AI Analysis button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-slate-600">
            <MessageSquare className="w-4 h-4" />
            <span>{responses.length} responses</span>
          </div>
          {avgLength > 0 && (
            <div className="flex items-center gap-1.5 text-slate-600">
              <Type className="w-4 h-4" />
              <span>~{avgLength} chars avg</span>
            </div>
          )}
        </div>
        {onAnalyze && texts.length >= 3 && (
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => onAnalyze(question.id)}
            className="text-xs gap-1.5"
          >
            <Lightbulb className="w-3.5 h-3.5" />
            AI Analysis
          </Button>
        )}
      </div>
      
      {/* Sentiment breakdown */}
      {sentimentBreakdown && (
        <div>
          <p className="text-xs text-slate-500 mb-2">Sentiment breakdown:</p>
          <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
            {sentimentBreakdown.positive > 0 && (
              <div 
                className="h-full transition-all" 
                style={{ width: `${sentimentBreakdown.positive}%`, backgroundColor: sentimentColors.positive }}
                title={`Positive: ${sentimentBreakdown.positive}%`}
              />
            )}
            {sentimentBreakdown.neutral > 0 && (
              <div 
                className="h-full transition-all" 
                style={{ width: `${sentimentBreakdown.neutral}%`, backgroundColor: sentimentColors.neutral }}
                title={`Neutral: ${sentimentBreakdown.neutral}%`}
              />
            )}
            {sentimentBreakdown.negative > 0 && (
              <div 
                className="h-full transition-all" 
                style={{ width: `${sentimentBreakdown.negative}%`, backgroundColor: sentimentColors.negative }}
                title={`Concerned: ${sentimentBreakdown.negative}%`}
              />
            )}
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span className="text-emerald-600">{sentimentBreakdown.positive}% Positive</span>
            <span className="text-slate-500">{sentimentBreakdown.neutral}% Neutral</span>
            <span className="text-red-500">{sentimentBreakdown.negative}% Concerned</span>
          </div>
        </div>
      )}
      
      {/* Sample responses */}
      <div className="space-y-2 max-h-[150px] overflow-y-auto">
        {texts.slice(0, 3).map((text, i) => (
          <div key={i} className="p-2 bg-slate-50 rounded text-sm text-slate-600 italic">
            "{text.substring(0, 120)}{text.length > 120 ? '...' : ''}"
          </div>
        ))}
        {texts.length > 3 && (
          <p className="text-xs text-slate-400 text-center">+{texts.length - 3} more responses</p>
        )}
      </div>
    </div>
  );
}

// Question result card (matching Driver Analysis style)
function QuestionResultCard({ question, responses, segmentFilter, onAnalyze }) {
  // Filter responses by segment if needed
  const filteredResponses = segmentFilter === 'all' 
    ? responses 
    : responses.filter(r => r.involvement === segmentFilter);
  
  const meetsThreshold = filteredResponses.length >= 3;
  
  return (
    <Card className={`transition-all ${!meetsThreshold ? 'opacity-60' : 'hover:shadow-md'}`}>
      <CardContent className="pt-5 pb-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-slate-100">
              <QuestionTypeIcon type={question.question_type} />
            </div>
            <Badge variant="outline" className="text-xs capitalize">
              {question.question_type?.replace('_', ' ')}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            {meetsThreshold ? (
              <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">
                {filteredResponses.length} responses
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-amber-50 text-amber-700">
                <ShieldAlert className="w-3 h-3 mr-1" />
                {filteredResponses.length} (below threshold)
              </Badge>
            )}
          </div>
        </div>
        
        {/* Question text */}
        <h4 className="font-semibold text-slate-900 mb-1">{question.heading}</h4>
        {question.text && question.text !== question.heading && (
          <p className="text-sm text-slate-500 mb-4 line-clamp-2">{question.text}</p>
        )}
        
        {/* Visualization based on question type */}
        {meetsThreshold && filteredResponses.length > 0 ? (
          <div className="mt-4">
            {(question.question_type === 'slider' || question.question_type === 'scale') && (
              <SliderVisualization responses={filteredResponses} config={question.slider_config} />
            )}
            {(question.question_type === 'multi_choice' || question.question_type === 'single_choice') && (
              <ChoiceVisualization responses={filteredResponses} multiSelect={false} />
            )}
            {question.question_type === 'checkbox' && (
              <ChoiceVisualization responses={filteredResponses} multiSelect={true} />
            )}
            {(question.question_type === 'free_text' || question.question_type === 'multi_topic') && (
              <FreeTextVisualization responses={filteredResponses} question={question} onAnalyze={onAnalyze} />
            )}
          </div>
        ) : (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg text-center text-slate-400 text-sm">
            {filteredResponses.length === 0 ? 'No responses yet' : 'Below anonymity threshold'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PublicAnalysisPage() {
  const { projectId } = useParams();
  const { currentProject, drivers } = useProject();
  const [loading, setLoading] = useState(true);
  const [publicData, setPublicData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState([]);
  const [involvementFilter, setInvolvementFilter] = useState('all');
  const [involvementOptions, setInvolvementOptions] = useState([]);
  
  // Segment filter state
  const [segmentTypes, setSegmentTypes] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [pendingSegments, setPendingSegments] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [uncategorizedCounts, setUncategorizedCounts] = useState({});
  const [stakeholders, setStakeholders] = useState([]);
  const [expandedTypes, setExpandedTypes] = useState({});
  
  // AI Analysis state
  const [aiAnalysisDialogOpen, setAiAnalysisDialogOpen] = useState(false);
  const [aiAnalysisData, setAiAnalysisData] = useState(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisQuestion, setAiAnalysisQuestion] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, questionsRes, responsesRes, segmentsRes, stakeholdersRes] = await Promise.all([
        api.get(`/projects/${projectId}/public-landing/stats`),
        api.get(`/projects/${projectId}/questions`),
        api.get(`/projects/${projectId}/public-landing/responses`),
        api.get(`/projects/${projectId}/segments-with-types`),
        api.get(`/projects/${projectId}/stakeholders?page_size=10000`)
      ]);

      setPublicData(statsRes.data);
      
      // Filter to only public landing questions
      const publicQuestions = questionsRes.data.filter(q => 
        q.question_context === 'public_landing'
      );
      setQuestions(publicQuestions);
      
      // Set responses
      const respData = responsesRes.data?.responses || responsesRes.data || [];
      setResponses(respData);
      
      // Extract unique involvement values
      const involvements = [...new Set(respData.map(r => r.involvement).filter(Boolean))];
      setInvolvementOptions(involvements);
      
      // Process segment data - API returns array of segment types
      const segData = segmentsRes.data || [];
      const segTypesWithSegments = Array.isArray(segData) ? segData : (segData.segment_types || []);
      setSegmentTypes(segTypesWithSegments);
      
      // Get stakeholders for public landing
      const stkData = stakeholdersRes.data?.stakeholders || stakeholdersRes.data || [];
      const publicStakeholders = stkData.filter(s => 
        s.source === 'self_identified' || s.source === 'anonymous' || s.is_anonymous
      );
      setStakeholders(publicStakeholders);
      
      // Calculate uncategorized counts per segment type
      const uncatCounts = {};
      segTypesWithSegments.forEach(st => {
        const segmentIds = (st.segments || []).map(s => s.id);
        
        // Count stakeholders that don't have any segment of this type
        const uncatCount = publicStakeholders.filter(s => {
          const assignments = s.segment_assignments || [];
          return !assignments.some(sa => segmentIds.includes(sa.segment_id));
        }).length;
        
        if (uncatCount > 0) {
          uncatCounts[st.id] = uncatCount;
        }
      });
      setUncategorizedCounts(uncatCounts);
      
    } catch (err) {
      console.error('Failed to fetch public landing data:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // AI Analysis handler
  const handleAiAnalysis = async (questionId) => {
    const question = questions.find(q => q.id === questionId);
    setAiAnalysisQuestion(question);
    setAiAnalysisDialogOpen(true);
    setAiAnalysisLoading(true);
    setAiAnalysisData(null);
    
    try {
      const res = await api.post(`/projects/${projectId}/questions/${questionId}/analyze-themes`);
      setAiAnalysisData(res.data);
    } catch (err) {
      console.error('Failed to analyze themes:', err);
      setAiAnalysisData({ error: 'Failed to generate AI analysis. Please try again.' });
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  // Flatten all segments for easy lookup
  const allSegments = segmentTypes.flatMap(st => st.segments || []);
  
  // For display - use actual applied filter state
  const isSegmentSelected = (segId) => selectedSegments.length === 0 || selectedSegments.includes(segId);
  
  // Get segment response count for display
  const getSegmentResponseCount = (segId) => {
    return stakeholders.filter(s => {
      const assignments = s.segment_assignments || [];
      return assignments.some(a => a.segment_id === segId);
    }).length;
  };
  
  // Filter control functions - use pending state until Apply
  const workingSegments = filterOpen ? pendingSegments : selectedSegments;
  
  const isPendingSegmentSelected = (segId) => workingSegments.length === 0 || workingSegments.includes(segId);
  
  const isTypeAllSelected = (typeId) => {
    const typeSegments = segmentTypes.find(st => st.id === typeId)?.segments || [];
    return workingSegments.length === 0 || typeSegments.every(s => workingSegments.includes(s.id));
  };
  
  const isTypePartialSelected = (typeId) => {
    if (workingSegments.length === 0) return false;
    const typeSegments = segmentTypes.find(st => st.id === typeId)?.segments || [];
    const selectedCount = typeSegments.filter(s => workingSegments.includes(s.id)).length;
    return selectedCount > 0 && selectedCount < typeSegments.length;
  };
  
  const toggleSegment = (segId) => {
    const current = workingSegments;
    if (current.length === 0) {
      // All selected, deselect this one
      setPendingSegments(allSegments.filter(s => s.id !== segId).map(s => s.id));
    } else if (current.includes(segId)) {
      const newIds = current.filter(id => id !== segId);
      setPendingSegments(newIds.length === 0 ? [] : newIds);
    } else {
      setPendingSegments([...current, segId]);
    }
  };
  
  const toggleSegmentType = (typeId) => {
    const typeSegments = segmentTypes.find(st => st.id === typeId)?.segments || [];
    const typeSegmentIds = typeSegments.map(s => s.id);
    const current = workingSegments;
    const allSelected = current.length === 0 || typeSegmentIds.every(id => current.includes(id));
    
    if (allSelected) {
      if (current.length === 0) {
        setPendingSegments(allSegments.filter(s => !typeSegmentIds.includes(s.id)).map(s => s.id));
      } else {
        const newIds = current.filter(id => !typeSegmentIds.includes(id));
        setPendingSegments(newIds.length === 0 ? [] : newIds);
      }
    } else {
      if (current.length === 0) return;
      const newIds = [...new Set([...current, ...typeSegmentIds])];
      setPendingSegments(newIds);
    }
  };
  
  const selectAllSegments = () => setPendingSegments([]);
  const deselectAllSegments = () => setPendingSegments(['__none__']);
  
  const applySegmentFilter = () => {
    setSelectedSegments(pendingSegments);
    setFilterOpen(false);
  };
  
  const cancelSegmentFilter = () => {
    setPendingSegments(selectedSegments);
    setFilterOpen(false);
  };
  
  const handleFilterOpenChange = (open) => {
    if (open) {
      setPendingSegments(selectedSegments);
      // Auto-expand all segment types for easier selection
      const allExpanded = {};
      segmentTypes.forEach(st => { allExpanded[st.id] = true; });
      setExpandedTypes(allExpanded);
    }
    setFilterOpen(open);
  };
  
  const toggleExpandType = (typeId) => {
    setExpandedTypes(prev => ({ ...prev, [typeId]: !prev[typeId] }));
  };

  const stats = publicData || {};
  const totalPublic = stats.total_public || 0;
  const pendingSelf = stats.pending_self || 0;
  const invitedSelf = stats.invited_self || 0;

  // Group responses by question
  const responsesByQuestion = {};
  responses.forEach(r => {
    const qId = r.question_id;
    if (!responsesByQuestion[qId]) {
      responsesByQuestion[qId] = [];
    }
    responsesByQuestion[qId].push(r);
  });
  
  // Helper function to check if a stakeholder matches the segment filter
  const stakeholderMatchesFilter = (stakeholderId) => {
    if (selectedSegments.length === 0) return true;
    
    const stakeholder = stakeholders.find(s => s.id === stakeholderId);
    if (!stakeholder) return true; // Include if stakeholder not found
    
    const assignments = stakeholder.segment_assignments || [];
    const assignedSegmentIds = assignments.map(a => a.segment_id);
    
    // Group selected segments by type
    const selectedByType = {};
    selectedSegments.forEach(segId => {
      if (segId.startsWith('uncategorized_')) {
        const typeId = segId.replace('uncategorized_', '');
        if (!selectedByType[typeId]) selectedByType[typeId] = { segments: [], hasUncategorized: false };
        selectedByType[typeId].hasUncategorized = true;
      } else {
        const segType = segmentTypes.find(st => 
          st.segments?.some(s => s.id === segId)
        );
        if (segType) {
          if (!selectedByType[segType.id]) selectedByType[segType.id] = { segments: [], hasUncategorized: false };
          selectedByType[segType.id].segments.push(segId);
        }
      }
    });
    
    // AND logic across types - must match at least one in EACH selected type
    for (const [typeId, { segments, hasUncategorized }] of Object.entries(selectedByType)) {
      const typeSegmentIds = segmentTypes.find(st => st.id === typeId)?.segments?.map(s => s.id) || [];
      const hasSegmentInType = assignedSegmentIds.some(sid => typeSegmentIds.includes(sid));
      
      // Check if matches any selected segment OR uncategorized
      const matchesSegment = segments.some(segId => assignedSegmentIds.includes(segId));
      const matchesUncategorized = hasUncategorized && !hasSegmentInType;
      
      if (!matchesSegment && !matchesUncategorized) {
        return false; // Doesn't match this type
      }
    }
    
    return true;
  };
  
  // Filter responses by segment selection
  const filteredResponsesByQuestion = {};
  Object.entries(responsesByQuestion).forEach(([qId, qResponses]) => {
    filteredResponsesByQuestion[qId] = qResponses.filter(r => 
      stakeholderMatchesFilter(r.stakeholder_id || r.respondent_id)
    );
  });

  // Calculate involvement breakdown
  const filteredResponses = responses.filter(r => 
    stakeholderMatchesFilter(r.stakeholder_id || r.respondent_id)
  );
  
  const involvementBreakdown = {};
  filteredResponses.forEach(r => {
    if (r.involvement) {
      involvementBreakdown[r.involvement] = (involvementBreakdown[r.involvement] || 0) + 1;
    }
  });

  const involvementChartData = Object.entries(involvementBreakdown)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Public Analysis
        </h1>
        <p className="text-sm sm:text-base text-slate-600 mt-1">
          Feedback from users accessing the public survey link
        </p>
      </div>

      {/* Warning Banner */}
      <Alert variant="warning" className="bg-amber-50 border-amber-200">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800">Unverified Data</AlertTitle>
        <AlertDescription className="text-amber-700">
          This data is from anonymous and self-identified users who accessed the public landing page. 
          Their identities and segment information have not been validated by an administrator.
        </AlertDescription>
      </Alert>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="total-public-card">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalPublic}</p>
                <p className="text-sm text-slate-500">Total Public</p>
              </div>
              <div className="p-3 bg-[#5AA2AE]/10 rounded-full">
                <FileText className="w-6 h-6 text-[#5AA2AE]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="pending-self-card">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-amber-600">{pendingSelf}</p>
                <p className="text-sm text-slate-500">Pending (Self)</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-full">
                <UserCheck className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="invited-self-card">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-emerald-600">{invitedSelf}</p>
                <p className="text-sm text-slate-500">Invited (Self)</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-full">
                <Send className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Self-Reported Involvement Breakdown */}
      {involvementChartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#5AA2AE]" />
              Self-Reported Involvement
            </CardTitle>
            <CardDescription>
              How respondents described their involvement with the project (unverified)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={involvementChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {involvementChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              {/* List */}
              <div className="space-y-2">
                {involvementChartData.map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-sm font-medium">{item.name}</span>
                    </div>
                    <Badge variant="secondary">{item.value}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Question Responses - Filtered by Involvement */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#5AA2AE]" />
                Public Landing Questions
              </CardTitle>
              <CardDescription>
                Responses to questions shown on the public landing page
              </CardDescription>
            </div>
            
            {/* Tree-Style Segment Filter */}
            <Popover open={filterOpen} onOpenChange={handleFilterOpenChange} modal={false}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <Filter className="w-4 h-4" />
                  Segments
                  {selectedSegments.length > 0 && selectedSegments[0] !== '__none__' && (
                    <Badge variant="secondary" className="ml-1 px-1.5 py-0">{selectedSegments.length}</Badge>
                  )}
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-0" onInteractOutside={(e) => e.preventDefault()}>
                {/* Select All / Deselect All */}
                <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
                  <span className="text-xs font-medium text-slate-600">Filter Segments</span>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2 text-xs"
                      onClick={(e) => { e.stopPropagation(); selectAllSegments(); }}
                    >
                      Select All
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2 text-xs text-slate-500"
                      onClick={(e) => { e.stopPropagation(); deselectAllSegments(); }}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                
                {/* Tree-style segment types */}
                <div className="max-h-64 overflow-y-auto py-1">
                  {segmentTypes.map(st => (
                    <div key={st.id} className="border-b border-slate-100 last:border-0">
                      {/* Segment Type Header (collapsible) */}
                      <div 
                        className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); toggleExpandType(st.id); }}
                      >
                        {expandedTypes[st.id] ? (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        )}
                        <Checkbox
                          checked={isTypeAllSelected(st.id)}
                          onCheckedChange={(e) => {
                            e?.stopPropagation?.();
                            toggleSegmentType(st.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className={isTypePartialSelected(st.id) ? 'data-[state=checked]:bg-slate-400' : ''}
                        />
                        <span className="text-sm font-medium flex-1">{st.name}</span>
                        <Badge variant="outline" className="text-xs px-1.5">
                          {(st.segments || []).filter(s => isPendingSegmentSelected(s.id)).length}/{(st.segments || []).length}
                        </Badge>
                      </div>
                      
                      {/* Segments (expandable) */}
                      {expandedTypes[st.id] && (
                        <div className="pl-8 pb-1">
                          {(st.segments || []).map(seg => (
                            <div 
                              key={seg.id}
                              className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer"
                              onClick={(e) => { e.stopPropagation(); toggleSegment(seg.id); }}
                            >
                              <Checkbox
                                checked={isPendingSegmentSelected(seg.id)}
                                onCheckedChange={() => toggleSegment(seg.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span className="text-sm flex-1">{seg.name}</span>
                              <span className="text-xs text-slate-400">
                                <Users className="w-3 h-3 inline mr-0.5" />
                                {getSegmentResponseCount(seg.id)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Apply / Cancel Footer */}
                <div className="flex items-center justify-end gap-2 px-3 py-2 border-t bg-slate-50">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 px-3 text-xs"
                    onClick={(e) => { e.stopPropagation(); cancelSegmentFilter(); }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    size="sm" 
                    className="h-7 px-3 text-xs bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                    onClick={(e) => { e.stopPropagation(); applySegmentFilter(); }}
                  >
                    Apply Filter
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filter Summary */}
          {selectedSegments.length > 0 && selectedSegments[0] !== '__none__' && (
            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg mb-4">
              <Filter className="w-4 h-4" />
              <span>Filtering by {selectedSegments.length} segment(s)</span>
              <span className="text-slate-400">|</span>
              <span className="font-medium">{filteredResponses.length} matching responses</span>
            </div>
          )}
          
          {questions.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No public landing questions configured</p>
              <p className="text-sm mt-2">Add questions in the Questions admin page with "Public Landing" context</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {questions.map(q => (
                <QuestionResultCard 
                  key={q.id}
                  question={q}
                  responses={filteredResponsesByQuestion[q.id] || []}
                  segmentFilter="all"
                  onAnalyze={handleAiAnalysis}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Anonymity Notice */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-amber-100">
              <ShieldAlert className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-sm">Data Quality Notice</p>
              <p className="text-sm text-slate-500">
                Public landing data should be used for directional insights only. 
                For validated analysis, use data from invited stakeholders in the Driver Analysis dashboard.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Analysis Dialog */}
      <Dialog open={aiAnalysisDialogOpen} onOpenChange={setAiAnalysisDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              AI Theme Analysis
            </DialogTitle>
            <DialogDescription>
              {aiAnalysisQuestion?.heading}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {aiAnalysisLoading ? (
              <div className="space-y-4 py-8">
                <div className="flex items-center justify-center gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#5AA2AE] border-t-transparent"></div>
                  <span className="text-slate-600">Analyzing responses...</span>
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              </div>
            ) : aiAnalysisData?.error ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Analysis Error</AlertTitle>
                <AlertDescription>{aiAnalysisData.error}</AlertDescription>
              </Alert>
            ) : aiAnalysisData ? (
              <div className="space-y-6">
                {/* Summary */}
                {aiAnalysisData.summary && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-900 mb-2">Summary</h4>
                    <p className="text-slate-600">{aiAnalysisData.summary}</p>
                  </div>
                )}
                
                {/* Themes */}
                {aiAnalysisData.themes && aiAnalysisData.themes.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Key Themes</h4>
                    <div className="space-y-3">
                      {aiAnalysisData.themes.map((theme, i) => (
                        <div key={i} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-slate-900">{theme.name || theme.theme}</span>
                            {theme.count && (
                              <Badge variant="secondary">{theme.count} mentions</Badge>
                            )}
                          </div>
                          {theme.description && (
                            <p className="text-sm text-slate-600">{theme.description}</p>
                          )}
                          {theme.examples && theme.examples.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {theme.examples.slice(0, 2).map((ex, j) => (
                                <p key={j} className="text-xs text-slate-500 italic">"{ex}"</p>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
